"use client";

import { Fragment, useRef, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Skeleton from "@mui/material/Skeleton";
import Grid from "@mui/material/Grid";
import { Divider } from "@mui/material";
import QuizIcon from "@mui/icons-material/Quiz";
import StartIcon from "@mui/icons-material/Start";

type TipoTest = "Sim" | "Ese" | "Seq";

interface SezioneObj {
    nomeParte?: string;
    numQuiz?: number;
    quizDisponibili?: number;
}

interface NewSezioneProps {
    sezioneObj?: SezioneObj;
    tipoTest: TipoTest;
    indexSezione: number;
    percentualeQuiz?: number | null;
    changeNumeroQuiz?: (numQuiz: number, index: number, quizDiPartenza: number) => void;
}

export default function NewSezione(props: NewSezioneProps) {
    const quizSelezionatiRef = useRef<HTMLInputElement>(null);
    const quizDiPartenzaRef = useRef<HTMLInputElement>(null);

    const [sezioneObj, setSezioneObj] = useState<SezioneObj | undefined>(undefined);
    const [numeroQuizSelezionati, setNumeroQuizSelezionati] = useState<number>(0);
    const [quizDiPartenza, setQuizDiPartenza] = useState<number>(0);
    const [maxQuizDiPartenza, setMaxQuizDiPartenza] = useState<number>(0);

    // Stati "transitori" solo per il movimento dello slider
    const [sliderQuizValue, setSliderQuizValue] = useState<number>(0);
    const [sliderPartenzaValue, setSliderPartenzaValue] = useState<number>(0);

    useEffect(() => {
        const sezioneObjTmp = props.sezioneObj;
        setSezioneObj(sezioneObjTmp);

        if (sezioneObjTmp?.numQuiz !== undefined) {
            const nQuizParsed = parseInt(sezioneObjTmp?.numQuiz.toString() ?? "0");
            const quizDisponobiliParsed = parseInt(sezioneObjTmp?.quizDisponibili?.toString() ?? "0");

            let nQuizSelezionatiTmp =
                nQuizParsed > quizDisponobiliParsed ? quizDisponobiliParsed : nQuizParsed;

            if (quizSelezionatiRef.current) {
                quizSelezionatiRef.current.value = nQuizSelezionatiTmp.toString();
            }

            setNumeroQuizSelezionati(nQuizSelezionatiTmp);
            setSliderQuizValue(nQuizSelezionatiTmp);
            setMaxQuizDiPartenza(quizDisponobiliParsed);
        }
    }, [props.sezioneObj]);

    function handleNumeroQuizCommit(
        _event: Event | React.SyntheticEvent,
        value: number | number[]
    ) {
        let numeroQuizSelezionatiTmp = Array.isArray(value) ? value[0] : value;

        if (!sezioneObj) return;

        // Limita al massimo disponibile
        if (numeroQuizSelezionatiTmp > (sezioneObj.quizDisponibili ?? 0)) {
            numeroQuizSelezionatiTmp = sezioneObj.quizDisponibili ?? 0;
        }

        if (props.tipoTest === "Seq" && sezioneObj.quizDisponibili) {
            let maxPartenzaTmp = sezioneObj.quizDisponibili - numeroQuizSelezionatiTmp;
            let tmpQuizPartenza = quizDiPartenza;

            if (tmpQuizPartenza > maxPartenzaTmp) tmpQuizPartenza = maxPartenzaTmp - 1;

            if (numeroQuizSelezionatiTmp < sezioneObj.quizDisponibili) {
                setQuizDiPartenza(tmpQuizPartenza);
                setSliderPartenzaValue(tmpQuizPartenza);
                setMaxQuizDiPartenza(maxPartenzaTmp);
                if (quizDiPartenzaRef.current) quizDiPartenzaRef.current.value = tmpQuizPartenza.toString();
            } else {
                setQuizDiPartenza(0);
                setSliderPartenzaValue(0);
                if (quizDiPartenzaRef.current) quizDiPartenzaRef.current.value = "0";
            }
        }

        if (quizSelezionatiRef.current) {
            quizSelezionatiRef.current.value = numeroQuizSelezionatiTmp.toString();
        }

        setNumeroQuizSelezionati(numeroQuizSelezionatiTmp);

        if (props.changeNumeroQuiz) {
            props.changeNumeroQuiz(numeroQuizSelezionatiTmp, props.indexSezione, quizDiPartenza);
        }
    }
    function handleNumeroQuizPartenzaCommit(_event: Event | React.SyntheticEvent, value: number | number[]) {
        let quizDiPartenzaTmp = Array.isArray(value) ? value[0] : value;

        if (quizDiPartenzaTmp < 0 || isNaN(quizDiPartenzaTmp)) quizDiPartenzaTmp = 0;

        setQuizDiPartenza(quizDiPartenzaTmp);
        setSliderPartenzaValue(quizDiPartenzaTmp);

        if (quizDiPartenzaRef.current) {
            quizDiPartenzaRef.current.value = quizDiPartenzaTmp.toString();
        }

        if (props.changeNumeroQuiz) {
            props.changeNumeroQuiz(numeroQuizSelezionati, props.indexSezione, quizDiPartenzaTmp);
        }
    }

    return (
        <Fragment>
            {sezioneObj ? (
                <Paper elevation={4} sx={{ height: "100%" }}>
                    <Box sx={{ minHeight: "100px", height: "100%" }}>
                        <Typography sx={{ textAlign: "center", p: 1 }} variant="body2">
                            <b>{sezioneObj?.nomeParte ?? ""}</b>
                        </Typography>
                        <Divider />
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                p: 3,
                            }}
                        >
                            {props.tipoTest === "Sim" && (
                                <Typography variant="body1" sx={{ color: "#003370" }}>
                                    Quesiti somministrati: <b>{sezioneObj?.numQuiz ?? 0}</b>
                                </Typography>
                            )}

                            {props.tipoTest === "Ese" && (
                                <Grid container alignItems="center">
                                    <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
                                        <QuizIcon sx={{ mr: 2, color: "#003370" }} />
                                        <Typography>Numero di quiz:</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <TextField
                                            onBlur={(e) => handleNumeroQuizCommit(e.nativeEvent, parseInt(e.target.value))}
                                            inputProps={{
                                                style: { textAlign: "center" },
                                                min: 1,
                                                max: sezioneObj.quizDisponibili,
                                            }}
                                            defaultValue={numeroQuizSelezionati}
                                            variant="outlined"
                                            inputRef={quizSelezionatiRef}
                                        />
                                    </Grid>
                                    <Grid item xs={2} sx={{ textAlign: "end" }}>
                                        <Typography>di {sezioneObj.quizDisponibili}</Typography>
                                    </Grid>
                                    <Grid item xs={12} >
                                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                                            <Slider
                                                disableSwap
                                                size="small"
                                                value={sliderQuizValue}
                                                min={0}
                                                max={sezioneObj.quizDisponibili ?? 0}
                                                onChange={(_, val) => setSliderQuizValue(Array.isArray(val) ? val[0] : val)}
                                                onChangeCommitted={handleNumeroQuizCommit}
                                                valueLabelDisplay="auto"
                                                sx={{
                                                    width: { xs: '80%', md: '100%' },
                                                    touchAction: "none",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}

                            {props.tipoTest === "Seq" && (
                                <Fragment>
                                    <Grid container alignItems="center">
                                        <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
                                            <QuizIcon sx={{ mr: 2, color: "#003370" }} />
                                            <Typography>Numero di quiz:</Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                onBlur={(e) =>
                                                    handleNumeroQuizCommit(e.nativeEvent, parseInt(e.target.value))
                                                }
                                                inputProps={{ style: { textAlign: "center" } }}
                                                defaultValue={numeroQuizSelezionati}
                                                variant="outlined"
                                                inputRef={quizSelezionatiRef}
                                            />
                                        </Grid>
                                        <Grid item xs={2} sx={{ textAlign: "end" }}>
                                            <Typography>di {sezioneObj.quizDisponibili ?? 0}</Typography>
                                        </Grid>
                                        <Grid item xs={12} >
                                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                <Slider
                                                    disableSwap
                                                    size="small"
                                                    value={sliderQuizValue}
                                                    min={0}
                                                    max={sezioneObj.quizDisponibili ?? 0}
                                                    onChange={(_, val) => setSliderQuizValue(Array.isArray(val) ? val[0] : val)}
                                                    onChangeCommitted={handleNumeroQuizCommit}
                                                    valueLabelDisplay="auto"
                                                    sx={{
                                                        width: { xs: '80%', md: '100%' },
                                                        touchAction: "none",
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Grid container alignItems="center" sx={{ mt: 2 }}>
                                        <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
                                            <StartIcon sx={{ mr: 2, color: "#003370" }} />
                                            <Typography>Parti dal quiz:</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                onBlur={(e) =>
                                                    handleNumeroQuizPartenzaCommit(e.nativeEvent, parseInt(e.target.value))
                                                }
                                                inputProps={{ style: { textAlign: "center" } }}
                                                defaultValue={quizDiPartenza}
                                                variant="outlined"
                                                inputRef={quizDiPartenzaRef}
                                            />
                                        </Grid>
                                        <Grid item xs={12} >
                                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                <Slider
                                                    size="small"
                                                    value={sliderPartenzaValue}
                                                    disabled={
                                                        numeroQuizSelezionati === (sezioneObj.quizDisponibili ?? 0) ||
                                                        numeroQuizSelezionati === 0
                                                    }
                                                    min={0}
                                                    max={maxQuizDiPartenza}
                                                    onChange={(_, val) =>
                                                        setSliderPartenzaValue(Array.isArray(val) ? val[0] : val)
                                                    }
                                                    disableSwap
                                                    sx={{
                                                        width: { xs: '80%', md: '100%' },
                                                        touchAction: "none",
                                                    }}
                                                    onChangeCommitted={handleNumeroQuizPartenzaCommit}
                                                    valueLabelDisplay="auto"
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ my: 1, width: "100%" }} />
                                    <Typography>
                                        Percentuale di completamento:{" "}
                                        <b>
                                            {props.percentualeQuiz !== undefined && props.percentualeQuiz !== null
                                                ? Math.min(100, Math.max(0, props.percentualeQuiz))
                                                : 0}
                                            %
                                        </b>
                                    </Typography>
                                </Fragment>
                            )}
                        </Box>
                    </Box>
                </Paper>
            ) : (
                <Skeleton
                    variant="rectangular"
                    sx={{ width: "100%", borderRadius: "10px" }}
                    height={150}
                />
            )}
        </Fragment>
    );
}
